import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Image,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {formattedURL} from '../../../utils/url'
import useTagManager from '../../../hooks/use-tag-manager'

import {
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
} from '../../../constants'

const getProductPricing = (product) => {
    const currency = product?.currency ?? 'USD'
    const currentPrice = product?.price ?? null
    const promo = product?.productPromotions?.[0]

    if (promo?.promotionalPrice != null && promo.promotionalPrice < currentPrice) {
        return {salePrice: promo.promotionalPrice, listPrice: currentPrice, currency}
    }

    return {salePrice: currentPrice, listPrice: null, currency}
}

const WishlistCarouselItem = ({wishlistItem, product, isMoving, onMoveToBag, index}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {formatNumber} = useIntl()
    const {salePrice, currency} = getProductPricing(product)
    const fmt = (price) =>
        price != null ? formatNumber(price, {style: 'currency', currency}) : null

    const tagManager = useTagManager()
    const isplp = false
    const isBloomReachSearch = false

    const {variants, c_colorMaterial, c_color} = product || {}

    const selectableAttributeId = product?.variationValues?.[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4]
        ? PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
        : product?.variationAttributes?.find((attr) =>
              [
                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
              ]?.includes(attr?.id)
          )?.id

    const initialVariationValue =
        variants?.find((v) => v.productId === product.id)?.variationValues?.[
            selectableAttributeId
        ] ||
        variants?.[0]?.variationValues?.[selectableAttributeId] ||
        c_colorMaterial ||
        c_color

    const [selectableAttributeValue, setSelectableAttributeValue] = useState(initialVariationValue)

    useEffect(() => {
        if (initialVariationValue) setSelectableAttributeValue(initialVariationValue)
    }, [initialVariationValue])

    let selectedVariant = product?.variants?.find(
        ({variationValues, productId}) =>
            productId ===
                (isBloomReachSearch || !isplp ? product.id : product?.representedProduct?.id) &&
            variationValues?.[selectableAttributeId] === selectableAttributeValue
    )

    if (!selectedVariant) {
        selectedVariant = product?.variants?.find(
            ({variationValues, productId}) =>
                variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                product?.c_allPdpLinks?.some((p) => p.id === productId)
        )
    }

    const productUrl = product?.productType?.set
        ? product?.c_url
        : product?.c_allPdpLinks?.find((p) => p.id === selectedVariant?.productId)?.url ||
          product?.slugUrl ||
          product?.c_url

    const shouldUseExternalRedirect = product?.productType?.set || product?.type?.set

    const productUrlFormatted = formattedURL(productUrl, shouldUseExternalRedirect)

    return (
        <Box
            {...cartStyles.wishlistInCart.item.wrapper}
            data-testid={`sf-cart-wishlist-item-${wishlistItem.productId}`}
        >
            {/* Thumbnail */}
            <Box
                {...productUrlFormatted}
                onClick={() =>
                    tagManager.productClick(
                        'sf-cart-wishlist-preview',
                        product,
                        selectedVariant,
                        index + 1,
                        null,
                        null,
                        'sf-cart-wishlist-preview',
                        'Trending Now'
                    )
                }
                position="relative"
                backgroundColor="gray.100"
                minHeight="250px"
                minWidth="175px"
            >
                <Image
                    src={product?.c_images?.mediumImage || '/noimage.png'}
                    alt={product?.name ?? ''}
                    width="175px"
                    height="250px"
                    mixBlendMode="multiply"
                    aspectRatio="175/250"
                    objectFit="unset"
                />
            </Box>

            <Text
                {...productUrlFormatted}
                onClick={() =>
                    tagManager.productClick(
                        'sf-cart-wishlist-preview',
                        product,
                        selectedVariant,
                        index + 1,
                        null,
                        null,
                        'sf-cart-wishlist-preview',
                        'Trending Now'
                    )
                }
                {...cartStyles.wishlistInCart.item.name}
                noOfLines={2}
            >
                {product?.name}
            </Text>

            <Flex align="baseline" flexWrap="wrap">
                {salePrice != null && (
                    <Text {...cartStyles.wishlistInCart.item.price}>{fmt(salePrice)}</Text>
                )}
            </Flex>

            <Button
                {...cartStyles.wishlistInCart.item.button}
                isLoading={isMoving}
                loadingText="Moving…"
                isDisabled={isMoving}
                onClick={() => onMoveToBag(wishlistItem, product, index + 1)}
            >
                <FormattedMessage defaultMessage="Move to Bag" id="cart.wishlist.cta.move_to_bag" />
            </Button>
        </Box>
    )
}

WishlistCarouselItem.propTypes = {
    wishlistItem: PropTypes.shape({
        id: PropTypes.string.isRequired,
        productId: PropTypes.string.isRequired,
        quantity: PropTypes.number
    }).isRequired,
    product: PropTypes.object,
    isMoving: PropTypes.bool,
    onMoveToBag: PropTypes.func.isRequired,
    index: PropTypes.number
}

WishlistCarouselItem.defaultProps = {
    product: undefined,
    isMoving: false
}

export default WishlistCarouselItem
